import { addCalendarDays, calgaryDateString } from '../../../../../../utils/calgaryTime';
import React, { useState, useEffect, useCallback, useMemo, useRef, useId } from 'react';
import './InvoiceWizard.css';
import { getRuntimeConfig, useRuntimeConfigStatus } from '@frontend/__settings__/runtimeConfig';
import InvoiceService from '../../../../../../AppSettings/services/InvoiceService';
import ClientService from '../../../../../../AppSettings/services/ClientService';
import MatterService from '../../../../../../AppSettings/services/MatterService';
import FinancialService from '../../../../../../AppSettings/services/FinancialService';
import VenExpService from '../../../../../../AppSettings/services/VenExpService';
import OppExpService from '../../../../../../AppSettings/services/OppExpService';
import GenExpensesService from '../../../../../../AppSettings/services/GenExpensesService';
import generateInvoicePDF from '../InvoiceGen/InvoicePDFGen';
import normalizeToArray from '../../../../../../helpers/responseHelpers';
import { computeBilledTimeAmount, parseTimeToHours } from '../../../../../../helpers/billingHelpers';
import {
  consolidateInvoiceLineItems,
  isGroupableInvoiceLineItem,
  linkedSourceIdsByType,
  isLinkedExpense,
  dedupeByKey,
} from '../../../../../../helpers/invoiceLineItems';

export interface InvoiceWizardData {
  clientId: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  clientAddress: string;
  clientCompany: string;
  matterId: string;
  matterName: string;
  invoiceNumber: string;
  issueDate: string;
  selectedBillableTimes: any[];
  selectedFixedFees: any[];
  selectedExpenses: any[];
  lineItems: any[];
  dueDate: string;
  poNumber: string;
  notesToClient: string;
  additionalRecipients: string;
  enableAutoReminders: boolean;
  discount: number;
  taxRate: number;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
}

interface LineItem {
  type: string;
  description: string;
  item: string;
  qty: number;
  amount: number;
  discount: number;
  id?: string;
  sourceType?: string;
}

interface InvoiceWizardProps {
  onClose: () => void;
  existingInvoice?: any;
  matterId?: string;
  readOnly?: boolean;
}

/** AFE in UI ↔ `matters.publication_no` (and snapshot on `invoices.publication_no`). */
function pickPublicationNo(matter: Record<string, unknown> | null | undefined): string {
  if (!matter || typeof matter !== 'object') return '';
  const raw =
    matter.publication_no ??
    (matter as { matter_publication_no?: string | null }).matter_publication_no;
  if (raw == null) return '';
  const s = String(raw).trim();
  return s;
}

/** LSD in UI ↔ `matters.registration_no` (and snapshot on `invoices.registration_no`). */
function pickRegistrationNo(matter: Record<string, unknown> | null | undefined): string {
  if (!matter || typeof matter !== 'object') return '';
  const raw =
    matter.registration_no ??
    (matter as { matter_registration_no?: string | null }).matter_registration_no;
  if (raw == null) return '';
  return String(raw).trim();
}

/** int4 (PostgreSQL serial) range. */
const INT32_MAX = 2_147_483_647;

function maxInvoiceIdFromList(invoices: unknown[]): number {
  let m = 0;
  for (const inv of invoices) {
    if (!inv || typeof inv !== 'object') continue;
    const rec = inv as { invoice_number?: unknown; invoiceid?: unknown };
    const id = rec.invoice_number ?? rec.invoiceid;
    const n = typeof id === 'number' ? id : parseInt(String(id ?? ''), 10);
    if (!Number.isNaN(n) && n > m) m = n;
  }
  return m;
}

/** Next value after the highest `invoiceid` in the list (aligns with global serial for new rows). */
function nextInvoiceIdStringFromList(invoices: unknown[]): string {
  const next = maxInvoiceIdFromList(invoices) + 1;
  return String(Math.min(next, INT32_MAX));
}

/**
 * Digits only, clamped to 0..INT32_MAX. Strips non-digits; empty allowed while clearing.
 * Normalizes to a plain decimal string (e.g. leading zeros from paste become one number).
 */
function sanitizeSerial4Input(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  let n = parseInt(digits, 10);
  if (Number.isNaN(n)) return '';
  n = Math.min(n, INT32_MAX);
  n = Math.max(n, 0);
  return String(n);
}

/** # field when editing: DB `invoiceid` (serial) is the only stored id. */
function displayInvoiceIdFromRow(row: { invoice_number?: unknown; invoiceid?: unknown } | null | undefined): string {
  if (!row) return '';
  const v = row.invoice_number ?? row.invoiceid;
  if (v == null) return '';
  return String(v).trim();
}

const InvoiceWizard: React.FC<InvoiceWizardProps> = ({ onClose, existingInvoice, matterId: propMatterId, readOnly = false }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [invoiceFrom, setInvoiceFrom] = useState<any>(null);
  const [showImportPicker, setShowImportPicker] = useState(false);

  // Sender defaults come from the tenant's runtime config (P1) when ANY sender field is
  // set — atomically, so a partially-filled row yields blanks rather than a letterhead
  // mixing this tenant's name with NCAL's address. The NCAL letterhead (incl. GST) stays
  // as the all-or-nothing fallback so unregistered deployments are unchanged.
  const sender = getRuntimeConfig()?.invoiceSender;
  const senderConfigured = Boolean(
    sender && (sender.name || sender.address || sender.email || sender.phone || sender.gstNumber),
  );
  const [fromName, setFromName] = useState(senderConfigured ? sender?.name ?? '' : 'NCAL Engineering');
  const [fromAddress, setFromAddress] = useState(senderConfigured ? sender?.address ?? '' : 'Nielsen, Clark & Associates Ltd.\no/a NCAL Engineering');
  const [fromPhone, setFromPhone] = useState(senderConfigured ? sender?.phone ?? '' : '(403) 460-5000');

  const [clientInfo, setClientInfo] = useState({
    id: '', name: '', email: '', phone: '', address: '', company: '',
  });
  const [matterName, setMatterName] = useState('');
  const [afeNumber, setAfeNumber] = useState('');
  const [lsdNumber, setLsdNumber] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [issueDate, setIssueDate] = useState(calgaryDateString());
  const [invoiceNumber, setInvoiceNumber] = useState(() => {
    if (existingInvoice) {
      const d = displayInvoiceIdFromRow(existingInvoice);
      if (d) return d;
    }
    return '';
  });
  const [dueDate, setDueDate] = useState(() => {
    return addCalendarDays(calgaryDateString(), 30);
  });
  const [discount, setDiscount] = useState(0);
  const [taxRate, setTaxRate] = useState(0);
  const [amountPaid, setAmountPaid] = useState(0);
  const [notesToClient, setNotesToClient] = useState(
    'Payment is due within 30 days. Thank you for your continued business.'
  );
  const [addDataDump, setAddDataDump] = useState(true);
  const [blackAndWhitePdf, setBlackAndWhitePdf] = useState(false);
  const [billingPeriodStart, setBillingPeriodStart] = useState('');
  const [billingPeriodEnd, setBillingPeriodEnd] = useState('');
  const [terms, setTerms] = useState('');
  const [gstNumber, setGstNumber] = useState(senderConfigured ? sender?.gstNumber ?? '' : '811818848RT0001');
  const [fromEmail, setFromEmail] = useState(senderConfigured ? sender?.email ?? '' : 'invoices@ncaleng.ca');

  const [manualLineItems, setManualLineItems] = useState<LineItem[]>([]);

  const [billableTimes, setBillableTimes] = useState<any[]>([]);
  const [fixedFees, setFixedFees] = useState<any[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);
  // Rows already linked to the invoice being edited — used for PDF data dump on
  // Update & Download (they are filtered out of the unbilled import lists).
  const [linkedDumpSources, setLinkedDumpSources] = useState<{
    billableTimes: any[];
    fixedFees: any[];
    expenses: any[];
  }>({ billableTimes: [], fixedFees: [], expenses: [] });
  const [selectedBTIds, setSelectedBTIds] = useState<Set<string>>(new Set());
  const [selectedFFIds, setSelectedFFIds] = useState<Set<string>>(new Set());
  const [selectedExpIds, setSelectedExpIds] = useState<Set<string>>(new Set());

  const effectiveMatterId = propMatterId || existingInvoice?.matterid || '';

  const fetchNextInvoiceIdString = useCallback(async (): Promise<string> => {
    const raw = await InvoiceService.getAllInvoices().catch(() => []);
    const list = normalizeToArray(raw);
    return nextInvoiceIdStringFromList(list);
  }, []);

  /** After any manual edit, we keep the field as-is and do not overwrite with a new auto-suggested id. */
  const userEditedInvoiceNumber = useRef(false);
  /** Once the user edits Tax, stop auto-syncing it to the GST default. */
  const userEditedTaxRate = useRef(false);
  const invoiceNumberHintId = useId();

  useEffect(() => {
    if (existingInvoice) return;
    if (userEditedInvoiceNumber.current) return;
    if (invoiceNumber !== '') return;
    let cancelled = false;
    fetchNextInvoiceIdString().then((next) => {
      if (cancelled) return;
      if (userEditedInvoiceNumber.current) return;
      setInvoiceNumber(next);
    });
    return () => { cancelled = true; };
  }, [existingInvoice, invoiceNumber, fetchNextInvoiceIdString]);

  /** GST is a flat 5%, so new invoices default to a 5% tax. Existing invoices keep their saved rate, and any manual edit stops the auto-default. */
  useEffect(() => {
    if (existingInvoice) return;
    if (userEditedTaxRate.current) return;
    setTaxRate(5);
  }, [existingInvoice]);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      setLoading(true);
      try {
        const [invoiceFromData, matterData] = await Promise.all([
          InvoiceService.getInvoiceFrom().catch(() => []),
          effectiveMatterId ? MatterService.getMatterDetails(effectiveMatterId).catch(() => null) : Promise.resolve(null),
        ]);
        if (cancelled) return;

        if (Array.isArray(invoiceFromData) && invoiceFromData.length > 0) {
          const f = invoiceFromData[0] as any;
          setInvoiceFrom(f);
          if (f.gst_number) setGstNumber(f.gst_number);
        }

        if (matterData) {
          setMatterName(matterData.mattername || matterData.matterid || '');
          const rec = matterData as unknown as Record<string, unknown>;
          const afeFromMatter = pickPublicationNo(rec);
          const lsdFromMatter = pickRegistrationNo(rec);
          if (!existingInvoice) {
            setAfeNumber(afeFromMatter);
            setLsdNumber(lsdFromMatter);
            setPoNumber(String(matterData.po_number ?? '').trim());
          } else {
            const storedAfe = existingInvoice.publication_no;
            const storedLsd = existingInvoice.registration_no;
            setAfeNumber(
              storedAfe != null && String(storedAfe).trim() !== ''
                ? String(storedAfe)
                : afeFromMatter
            );
            setLsdNumber(
              storedLsd != null && String(storedLsd).trim() !== ''
                ? String(storedLsd)
                : lsdFromMatter
            );
          }
          if (matterData.clientid) {
            try {
              const c = await ClientService.getClientById(matterData.clientid);
              if (cancelled) return;
              if (c) {
                setClientInfo({
                  id: c.ClientID || c.client_id || '',
                  name: `${c.FirstName || c.first_name || ''} ${c.LastName || c.last_name || ''}`.trim() || 'Unnamed Client',
                  email: c.Email || c.email || '',
                  phone: c.Phone || c.phone || '',
                  address: c.Address || c.address || '',
                  company: c.Company || c.company || '',
                });
              }
            } catch { /* continue without client */ }
          }
        }

        if (effectiveMatterId) {
          const [btData, ffData, venData, oppData, genData, disbData] = await Promise.all([
            FinancialService.getBilledTime(effectiveMatterId).catch(() => []),
            FinancialService.getAssignedFixedFees(effectiveMatterId).catch(() => []),
            VenExpService.getVenExpensesByMatter(effectiveMatterId).catch(() => []),
            OppExpService.getOppExpensesByMatter(effectiveMatterId).catch(() => []),
            GenExpensesService.getGenExpensesByMatter(effectiveMatterId).catch(() => []),
            FinancialService.getAgentDisbursements(effectiveMatterId).catch(() => []),
          ]);
          if (cancelled) return;

          const allBT = normalizeToArray(btData)
            .filter(Boolean)
            .filter((i: any) => i.is_billable !== false); // A3: exclude non-billable time from invoice import
          const filteredBT = allBT.filter((i: any) => !i.invoiceid);
          const allFF = normalizeToArray(ffData).filter(Boolean);
          const filteredFF = allFF.filter((i: any) => !i.invoiceid);
          // Normalize each expense so invoice state is a single boolean regardless of source table.
          // ven/opp tables store a boolean `invoiced` column; gen/disbursement tables use an `invoiceid` FK.
          const withInvoiceFlag = (exp: any) => ({
            ...exp,
            _isInvoiced: Boolean(exp.invoiceid || exp.invoiced),
          });
          const expRaw = [
            ...normalizeToArray(venData),
            ...normalizeToArray(oppData),
            ...normalizeToArray(genData),
            ...normalizeToArray(disbData),
          ].filter(Boolean).map(withInvoiceFlag);
          const allExp = expRaw.filter((exp) => !exp._isInvoiced);

          setBillableTimes(filteredBT);
          setFixedFees(filteredFF);
          setExpenses(allExp);

          if (existingInvoice) {
            const linkedIds = linkedSourceIdsByType(existingInvoice.line_items);
            setLinkedDumpSources({
              billableTimes: allBT.filter((i: any) =>
                linkedIds.get('billed time')?.has(String(i.bt_id))
              ),
              fixedFees: allFF.filter((i: any) =>
                linkedIds.get('fixed fee')?.has(String(i.assignedid))
              ),
              expenses: expRaw.filter((exp) => isLinkedExpense(exp, linkedIds)),
            });
            // Keep import selections empty on edit; user can add new unbilled items.
            setSelectedBTIds(new Set());
            setSelectedFFIds(new Set());
            setSelectedExpIds(new Set());
          } else {
            setLinkedDumpSources({ billableTimes: [], fixedFees: [], expenses: [] });
            setSelectedBTIds(new Set(filteredBT.map((i: any) => i.bt_id)));
            setSelectedFFIds(new Set(filteredFF.map((i: any) => i.assignedid)));
            setSelectedExpIds(new Set(allExp.map((i: any) => getExpId(i))));
          }
        }
      } catch (err) {
        console.error('Error initializing invoice creator:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    init();
    return () => { cancelled = true; };
  }, [effectiveMatterId, existingInvoice]);

  useEffect(() => {
    if (existingInvoice) {
      const d = displayInvoiceIdFromRow(existingInvoice);
      setInvoiceNumber(d || '1');
      setIssueDate(existingInvoice.created_at?.split('T')[0] || calgaryDateString());
      setDueDate(existingInvoice.due_date || dueDate);
      setDiscount(parseFloat(existingInvoice.discount) || 0);
      setTaxRate(parseFloat(existingInvoice.tax_rate) || 0);
      setAmountPaid(parseFloat(existingInvoice.amount_paid) || 0);

      if (existingInvoice.line_items) {
        setManualLineItems(existingInvoice.line_items.map((li: any) => ({
          type: li.type || 'money_in',
          description: li.description || '',
          item: li.item || '',
          qty: parseFloat(li.qty) || 1,
          amount: parseFloat(li.amount) || 0,
          discount: parseFloat(li.discount) || 0,
          // Preserve the link keys so an update re-stamps invoiceid onto the
          // source rows instead of orphaning them (update_invoice nulls all
          // linked rows, then re-links from these; without id/sourceType the
          // re-link is a no-op and the Invoice # disappears from exports).
          id: li.id,
          sourceType: li.sourceType,
        })));
      }
      if (existingInvoice.billing_period_start) setBillingPeriodStart(existingInvoice.billing_period_start);
      if (existingInvoice.billing_period_end) setBillingPeriodEnd(existingInvoice.billing_period_end);
      if (existingInvoice.terms) setTerms(existingInvoice.terms);
      if (existingInvoice.gst_number) setGstNumber(existingInvoice.gst_number);
      setPoNumber(String(existingInvoice.po_number ?? ''));
      // Unconditional: an invoice saved without notes (NULL) opens empty, not with the new-invoice default.
      setNotesToClient(existingInvoice.notes_to_client ?? '');

      if (existingInvoice.client) {
        setClientInfo({
          id: existingInvoice.clientid || '',
          name: existingInvoice.client.name || '',
          email: existingInvoice.client.email || '',
          phone: existingInvoice.client.phone || '',
          address: existingInvoice.client.address || '',
          company: existingInvoice.client.company || '',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingInvoice]);

  const getExpId = (item: any) => item.disbursementid || item.ven_id || item.opp_id || item.gen_id || item.ge_id || '';

  /** sourceType for invoice line_items so backend updates the correct table's invoiceid */
  const getExpSourceType = (item: any): string => {
    if (item.ge_id != null || item.gen_id != null) return 'gen expense';
    if (item.opp_id != null) return 'opp expense';
    if (item.ven_id != null) return 'ven expense';
    return 'disbursement';
  };

  const toggleBT = (id: string) => {
    setSelectedBTIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleFF = (id: string) => {
    setSelectedFFIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleExp = (id: string) => {
    setSelectedExpIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const isInBillingPeriod = useCallback((record: any): boolean => {
    if (!billingPeriodStart || !billingPeriodEnd) return true;
    const dateStr = record.date_recorded || record.date_added || record.date || record.expense_date || record.created_at || '';
    if (!dateStr) return true;
    const d = dateStr.split('T')[0];
    return d >= billingPeriodStart && d <= billingPeriodEnd;
  }, [billingPeriodStart, billingPeriodEnd]);

  const importedLineItems = useMemo(() => {
    const items: LineItem[] = [];
    billableTimes
      .filter(bt => selectedBTIds.has(bt.bt_id) && isInBillingPeriod(bt) && parseTimeToHours(bt.time_recorded) > 0)
      .forEach(bt => {
        const hours = parseTimeToHours(bt.time_recorded);
        const total = computeBilledTimeAmount(bt);
        items.push({
          type: 'money_in',
          description: bt.employee_name || 'Unknown',
          item: bt.notes || '',
          qty: hours,
          // Effective per-hour rate so qty × amount reproduces the true total
          amount: total / hours,
          discount: 0,
          id: bt.bt_id,
          sourceType: 'billed time',
        });
      });
    fixedFees.filter(ff => selectedFFIds.has(ff.assignedid) && isInBillingPeriod(ff)).forEach(ff => {
      const narrative = String(ff.narrative || '').trim();
      const feeCode = String(ff.fee_code || '').trim();
      items.push({
        type: 'money_in',
        description: narrative || 'Fixed fee',
        item: feeCode,
        qty: 1,
        amount: parseFloat(ff.fees) || 0,
        discount: 0,
        id: ff.assignedid,
        sourceType: 'fixed fee',
      });
    });
    expenses.filter(exp => selectedExpIds.has(getExpId(exp)) && isInBillingPeriod(exp)).forEach(exp => {
      items.push({
        type: 'money_out',
        description: exp.description || 'Disbursements',
        item: 'Disbursements',
        qty: typeof exp.quantity === 'number' ? exp.quantity : 1,
        amount: typeof exp.amount === 'number' ? exp.amount : parseFloat(exp.amount || exp.fees || '0') || 0,
        discount: 0,
        id: getExpId(exp),
        sourceType: getExpSourceType(exp),
      });
    });
    return items;
  }, [billableTimes, fixedFees, expenses, selectedBTIds, selectedFFIds, selectedExpIds, isInBillingPeriod]);

  const allLineItems = useMemo(() => {
    return [...importedLineItems, ...manualLineItems];
  }, [importedLineItems, manualLineItems]);

  // Groupable saved rows (billed time / fixed fee with a cost code) are shown
  // consolidated like create-mode imports. Non-groupable manuals stay editable.
  const editableManualIndices = useMemo(() => {
    return manualLineItems
      .map((li, i) => (isGroupableInvoiceLineItem(li) ? -1 : i))
      .filter((i) => i >= 0);
  }, [manualLineItems]);

  const displayImportedItems = useMemo(() => {
    const groupableSaved = manualLineItems.filter(isGroupableInvoiceLineItem);
    return consolidateInvoiceLineItems([
      ...importedLineItems,
      ...groupableSaved,
    ]) as LineItem[];
  }, [importedLineItems, manualLineItems]);

  const displayAllLineItems = useMemo(() => {
    const editableManuals = editableManualIndices.map((i) => manualLineItems[i]);
    return [...displayImportedItems, ...editableManuals];
  }, [displayImportedItems, editableManualIndices, manualLineItems]);

  const totals = useMemo(() => {
    let subtotal = 0;
    allLineItems.forEach(item => {
      // A20: foot the per-line DISPLAYED amount (cent-rounded), matching the main InvoiceWizard and
      // the server's invoice_totals check; unrounded sub-cent lines drift the total off by cents.
      const amt = Math.round((item.amount || 0) * (item.qty || 1) * 100) / 100;
      const disc = amt * ((item.discount || 0) / 100);
      subtotal += amt - disc;
    });
    const discountAmt = subtotal * (discount / 100);
    const afterDiscount = subtotal - discountAmt;
    const tax = afterDiscount * (taxRate / 100);
    const total = afterDiscount + tax;
    const balanceDue = total - amountPaid;
    return { subtotal, discountAmt, afterDiscount, tax, total, balanceDue };
  }, [allLineItems, discount, taxRate, amountPaid]);

  const addManualLineItem = () => {
    setManualLineItems(prev => [...prev, { type: 'money_in', description: '', item: '', qty: 1, amount: 0, discount: 0 }]);
  };

  const updateManualLineItem = (index: number, field: keyof LineItem, value: any) => {
    setManualLineItems(prev => prev.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const removeManualLineItem = (index: number) => {
    setManualLineItems(prev => prev.filter((_, i) => i !== index));
  };

  const totalItemCount = billableTimes.length + fixedFees.length + expenses.length;
  const selectedImportCount = selectedBTIds.size + selectedFFIds.size + selectedExpIds.size;

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      let refNo = invoiceNumber.trim();
      if (!refNo) {
        refNo = await fetchNextInvoiceIdString();
      }
      const pub = afeNumber.trim();
      const lsd = lsdNumber.trim();
      const po = poNumber.trim();
      const invoiceData: any = {
        matterid: effectiveMatterId,
        clientid: clientInfo.id,
        due_date: dueDate || null,
        discount,
        tax_rate: taxRate,
        total_amount: totals.total,
        line_items: allLineItems,
        acc_rec_total: totals.subtotal,
        acc_pay_total: 0,
        amount_paid: String(amountPaid || 0),
        publication_no: pub || null,
        registration_no: lsd || null,
        po_number: po || null,
        billing_period_start: billingPeriodStart || null,
        billing_period_end: billingPeriodEnd || null,
        // '' (not null) when cleared: the PUT drops null keys, and the server stores '' as NULL.
        notes_to_client: notesToClient.trim(),
      };

      if (existingInvoice?.invoiceid) {
        invoiceData.invoiceid = existingInvoice.invoiceid;
        await InvoiceService.updateInvoice(invoiceData);
      } else {
        const rid = parseInt(refNo, 10);
        if (!Number.isNaN(rid) && rid >= 1 && rid <= INT32_MAX) {
          invoiceData.requested_invoice_number = rid;
        }
        await InvoiceService.createInvoice(invoiceData);
      }

      const formatPeriodDate = (d: string) => {
        if (!d) return '';
        const parts = d.split('-');
        if (parts.length !== 3) return d;
        const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        return `${months[parseInt(parts[1]) - 1]} ${parseInt(parts[2])}, ${parts[0]}`;
      };
      const periodStr = billingPeriodStart && billingPeriodEnd
        ? `${formatPeriodDate(billingPeriodStart)} - ${formatPeriodDate(billingPeriodEnd)}`
        : '';

      try {
        const selectedDumpBT = billableTimes
          .filter(bt => selectedBTIds.has(bt.bt_id) && isInBillingPeriod(bt) && parseTimeToHours(bt.time_recorded) > 0);
        const selectedDumpFF = fixedFees.filter(
          ff => selectedFFIds.has(ff.assignedid) && isInBillingPeriod(ff)
        );
        const selectedDumpExp = expenses.filter(
          exp => selectedExpIds.has(getExpId(exp)) && isInBillingPeriod(exp)
        );

        const mapBtAmount = (bt: any) => {
          const stored = parseFloat(bt.amount);
          if (stored && stored !== 0) return { ...bt, stored_amount: stored };
          const tr = bt.time_recorded || '';
          let h = 0;
          if (tr.includes(':')) {
            const p = tr.split(':');
            h = (parseInt(p[0] || '0', 10) || 0) + (parseInt(p[1] || '0', 10) || 0) / 60 + (parseInt(p[2] || '0', 10) || 0) / 3600;
          } else {
            h = parseFloat(tr) || 0;
          }
          return { ...bt, stored_amount: h * (parseFloat(bt.billedrate) || 0) };
        };

        // On update, already-linked source rows are not in the unbilled lists —
        // merge them back in so "Add data dump" still produces detail pages.
        const dataDumpItems = addDataDump ? {
          billableTimes: dedupeByKey(
            [...linkedDumpSources.billableTimes, ...selectedDumpBT],
            (bt) => String(bt.bt_id)
          )
            .filter((bt) => parseTimeToHours(bt.time_recorded) > 0)
            .map(mapBtAmount),
          fixedFees: dedupeByKey(
            [...linkedDumpSources.fixedFees, ...selectedDumpFF],
            (ff) => String(ff.assignedid)
          ),
          expenses: dedupeByKey(
            [...linkedDumpSources.expenses, ...selectedDumpExp],
            (exp) => String(getExpId(exp))
          ),
        } : null;

        generateInvoicePDF(
          effectiveMatterId,
          { from_name: fromName, address: fromAddress, phone: fromPhone, email: fromEmail },
          {
            id: clientInfo.id,
            name: clientInfo.name,
            email: clientInfo.email,
            phone: clientInfo.phone,
            address: clientInfo.address,
            company: clientInfo.company,
          },
          dueDate, matterName, displayAllLineItems, discount, taxRate,
          refNo, afeNumber, '', null,
          null, periodStr, terms, gstNumber, issueDate, dataDumpItems,
          blackAndWhitePdf, amountPaid, poNumber, lsdNumber, notesToClient
        );
      } catch (pdfErr) {
        console.error('PDF generation error:', pdfErr);
      }

      onClose();
    } catch (error: any) {
      console.error('Error saving invoice:', error);
      const data = error?.data;
      const detail =
        (typeof data?.error === 'string' && data.error) ||
        (data?.errors && typeof data.errors === 'object'
          ? Object.entries(data.errors)
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
              .join('; ')
          : '') ||
        error?.message ||
        '';
      alert(`Failed to save invoice.${detail ? ' ' + detail : ''} Please try again.`);
    } finally {
      setIsSaving(false);
    }
  };

  const fmt = (n: number) => n.toFixed(2);

  if (loading) {
    return (
      <div className="inv-overlay" onClick={onClose}>
        <div className="inv-doc" onClick={e => e.stopPropagation()}>
          <div className="inv-loading"><div className="inv-spinner" /><p>Loading...</p></div>
        </div>
      </div>
    );
  }

  return (
    <div className="inv-overlay" onClick={onClose}>
      <div className="inv-doc" onClick={e => e.stopPropagation()}>

        {/* ── Top Row: From + Invoice Title ── */}
        <div className="inv-top">
          <div className="inv-from">
            <textarea
              className="inv-input inv-from-name"
              value={fromName}
              onChange={e => setFromName(e.target.value)}
              placeholder="Your Company Name"
              rows={2}
            />
            <textarea
              className="inv-input inv-from-address"
              value={fromAddress}
              onChange={e => setFromAddress(e.target.value)}
              placeholder="Your Address"
              rows={2}
            />
            <input
              className="inv-input inv-from-email"
              value={fromEmail}
              onChange={e => setFromEmail(e.target.value)}
              placeholder="Email"
            />
            <input
              className="inv-input inv-from-phone"
              value={fromPhone}
              onChange={e => setFromPhone(e.target.value)}
              placeholder="Phone"
            />
          </div>
          <div className="inv-title-block">
            <h1 className="inv-title">INVOICE</h1>
            <div className="inv-number-block">
              <div className="inv-number-row">
                <span className="inv-label">#</span>
                <input
                  className="inv-input inv-number-input"
                  name="sept-invoice-identifier"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  data-form-type="other"
                  value={invoiceNumber}
                  onChange={e => {
                    userEditedInvoiceNumber.current = true;
                    setInvoiceNumber(sanitizeSerial4Input(e.target.value));
                  }}
                  readOnly={readOnly}
                  placeholder="e.g. 482193"
                  aria-describedby={readOnly ? undefined : invoiceNumberHintId}
                />
              </div>
              {!readOnly && (
                <p className="inv-number-hint" id={invoiceNumberHintId}>
                  Digits only (0–2,147,483,647). On create, this value is saved as the invoice id if
                  that id is not already used; otherwise you will see an error. Auto-filled as last id + 1.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ── Bill To + Details ── */}
        <div className="inv-meta">
          <div className="inv-bill-to">
            <span className="inv-label">Bill To</span>
            <input
              className="inv-input"
              value={clientInfo.address}
              onChange={e => setClientInfo(p => ({ ...p, address: e.target.value }))}
              placeholder="Address"
            />
            <div className="inv-client-selected-row">
              <span className="inv-attention-label">Attention:</span>
              <input
                className="inv-input"
                value={clientInfo.name}
                onChange={e => setClientInfo(p => ({ ...p, name: e.target.value }))}
                placeholder="Client Name"
              />
            </div>
            {clientInfo.id ? (
              <span className="inv-client-id">{clientInfo.id}</span>
            ) : null}
            <input
              className="inv-input"
              value={clientInfo.company}
              onChange={e => setClientInfo(p => ({ ...p, company: e.target.value }))}
              placeholder="Client Company"
            />
            <input
              className="inv-input"
              value={clientInfo.email}
              onChange={e => setClientInfo(p => ({ ...p, email: e.target.value }))}
              placeholder="Email"
            />
          </div>
          <div className="inv-details">
            {matterName && (
              <div className="inv-detail-row">
                <span className="inv-label">Project</span>
                <span className="inv-detail-value">{matterName}</span>
              </div>
            )}
            <div className="inv-detail-row">
              <span className="inv-label">AFE</span>
              <input className="inv-input" value={afeNumber} onChange={e => setAfeNumber(e.target.value)} placeholder="e.g. AFE-001" />
            </div>
            <div className="inv-detail-row">
              <span className="inv-label">LSD</span>
              <input className="inv-input" value={lsdNumber} onChange={e => setLsdNumber(e.target.value)} placeholder="e.g. LSD-001" />
            </div>
            <div className="inv-detail-row">
              <span className="inv-label">PO</span>
              <input className="inv-input" value={poNumber} onChange={e => setPoNumber(e.target.value)} placeholder="e.g. PO-001" />
            </div>
            <div className="inv-detail-row">
              <span className="inv-label">Date</span>
              <input type="date" className="inv-input" value={issueDate} onChange={e => setIssueDate(e.target.value)} />
            </div>
            <div className="inv-detail-row">
              <span className="inv-label">Due Date</span>
              <input type="date" className="inv-input" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </div>
            <div className="inv-detail-row">
              <span className="inv-label">Terms</span>
              <input className="inv-input" value={terms} onChange={e => setTerms(e.target.value)} placeholder="e.g. Net 30" />
            </div>
            <div className="inv-detail-row inv-balance-row">
              <span className="inv-label">Balance Due</span>
              <span className="inv-balance">${fmt(totals.balanceDue)}</span>
            </div>
          </div>
        </div>

        {/* ── Billing Period ── */}
        <div className="inv-billing-period">
          <span className="inv-label">Billing Period</span>
          <div className="inv-period-inputs">
            <input type="date" className="inv-input" value={billingPeriodStart} onChange={e => setBillingPeriodStart(e.target.value)} />
            <span className="inv-period-sep">to</span>
            <input type="date" className="inv-input" value={billingPeriodEnd} onChange={e => setBillingPeriodEnd(e.target.value)} />
          </div>
        </div>

        {/* ── Line Items Table ── */}
        <table className="inv-table">
          <thead>
            <tr>
              <th className="inv-th-name">Name</th>
              <th className="inv-th-code">Cost code</th>
              <th className="inv-th-hours">Hours</th>
              <th className="inv-th-rate">Rate</th>
              <th className="inv-th-amount">Amount ($)</th>
              <th className="inv-th-action"></th>
            </tr>
          </thead>
          <tbody>
            {displayImportedItems.map((item, i) => (
              <tr key={`imp-${i}`} className="inv-row">
                <td><span className="inv-cell-text">{item.description}</span></td>
                <td><span className="inv-cell-text">{item.item || '—'}</span></td>
                <td className="inv-cell-num">{item.qty}</td>
                <td className="inv-cell-num">${fmt(item.amount)}</td>
                <td className="inv-cell-num">${fmt(item.amount * item.qty)}</td>
                <td></td>
              </tr>
            ))}
            {editableManualIndices.map((i) => {
              const item = manualLineItems[i];
              return (
              <tr key={`man-${i}`} className="inv-row inv-row-editable">
                <td>
                  <input
                    className="inv-input inv-cell-input"
                    value={item.description}
                    onChange={e => updateManualLineItem(i, 'description', e.target.value)}
                    placeholder="Name"
                  />
                </td>
                <td>
                  <input
                    className="inv-input inv-cell-input"
                    value={item.item}
                    onChange={e => updateManualLineItem(i, 'item', e.target.value)}
                    placeholder="Cost code"
                  />
                </td>
                <td>
                  <input
                    className="inv-input inv-cell-input inv-cell-input-sm"
                    type="number"
                    min="0"
                    step="0.5"
                    value={item.qty}
                    onChange={e => updateManualLineItem(i, 'qty', parseFloat(e.target.value) || 0)}
                  />
                </td>
                <td>
                  <input
                    className="inv-input inv-cell-input inv-cell-input-sm"
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.amount}
                    onChange={e => updateManualLineItem(i, 'amount', parseFloat(e.target.value) || 0)}
                  />
                </td>
                <td className="inv-cell-num">${fmt((item.amount || 0) * (item.qty || 1))}</td>
                <td>
                  <button className="inv-remove-btn" onClick={() => removeManualLineItem(i)} title="Remove">
                    &times;
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>

        {/* ── Actions below table ── */}
        <div className="inv-table-actions">
          <button className="inv-add-line" onClick={addManualLineItem}>+ Line Item</button>
          {totalItemCount > 0 && (
            <button
              className="inv-import-btn"
              onClick={() => setShowImportPicker(!showImportPicker)}
            >
              {showImportPicker ? 'Hide' : 'Import'} Unbilled Items ({selectedImportCount}/{totalItemCount})
            </button>
          )}
        </div>

        {/* ── Import Picker ── */}
        {showImportPicker && totalItemCount > 0 && (
          <div className="inv-import-picker">
            {billableTimes.length > 0 && (
              <div className="inv-import-group">
                <div className="inv-import-group-header">
                  <strong>Billable Time</strong>
                  <button
                    className="inv-import-toggle-all"
                    onClick={() => {
                      const allIds = new Set(billableTimes.map((i: any) => i.bt_id));
                      setSelectedBTIds(prev => prev.size === allIds.size ? new Set() : allIds);
                    }}
                  >
                    {selectedBTIds.size === billableTimes.length ? 'Remove All' : 'Add All'}
                  </button>
                </div>
                {billableTimes.map((item: any) => (
                  <label key={item.bt_id} className="inv-import-item">
                    <input type="checkbox" checked={selectedBTIds.has(item.bt_id)} onChange={() => toggleBT(item.bt_id)} />
                    <span className="inv-import-desc">{item.notes || `${item.employee_name || 'Unknown'} - ${item.time_recorded || ''}`}</span>
                    <span className="inv-import-amt">${fmt(computeBilledTimeAmount(item))}</span>
                  </label>
                ))}
              </div>
            )}
            {fixedFees.length > 0 && (
              <div className="inv-import-group">
                <div className="inv-import-group-header">
                  <strong>Fixed Fees</strong>
                  <button
                    className="inv-import-toggle-all"
                    onClick={() => {
                      const allIds = new Set(fixedFees.map((i: any) => i.assignedid));
                      setSelectedFFIds(prev => prev.size === allIds.size ? new Set() : allIds);
                    }}
                  >
                    {selectedFFIds.size === fixedFees.length ? 'Remove All' : 'Add All'}
                  </button>
                </div>
                {fixedFees.map((item: any) => (
                  <label key={item.assignedid} className="inv-import-item">
                    <input type="checkbox" checked={selectedFFIds.has(item.assignedid)} onChange={() => toggleFF(item.assignedid)} />
                    <span className="inv-import-desc">{item.fee_code} - {item.narrative}</span>
                    <span className="inv-import-amt">${fmt(parseFloat(item.fees || '0'))}</span>
                  </label>
                ))}
              </div>
            )}
            {expenses.length > 0 && (
              <div className="inv-import-group">
                <div className="inv-import-group-header">
                  <strong>Expenses</strong>
                  <button
                    className="inv-import-toggle-all"
                    onClick={() => {
                      const allIds = new Set(expenses.map((i: any) => getExpId(i)));
                      setSelectedExpIds(prev => prev.size === allIds.size ? new Set() : allIds);
                    }}
                  >
                    {selectedExpIds.size === expenses.length ? 'Remove All' : 'Add All'}
                  </button>
                </div>
                {expenses.map((item: any) => {
                  const id = getExpId(item);
                  return (
                    <label key={id} className="inv-import-item">
                      <input type="checkbox" checked={selectedExpIds.has(id)} onChange={() => toggleExp(id)} />
                      <span className="inv-import-desc">{item.description || 'Expense'}</span>
                      <span className="inv-import-amt">${fmt(typeof item.amount === 'number' ? item.amount : parseFloat(item.amount || item.fees || '0') || 0)}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Totals ── */}
        <div className="inv-totals">
          <div className="inv-totals-left">
            <div className="inv-options">
              <label className="inv-option">
                <input type="checkbox" checked={blackAndWhitePdf} onChange={e => setBlackAndWhitePdf(e.target.checked)} />
                <span>Black & white PDF</span>
              </label>
              <label className="inv-option">
                <input type="checkbox" checked={addDataDump} onChange={e => setAddDataDump(e.target.checked)} />
                <span>Add data dump</span>
              </label>
            </div>
          </div>
          <div className="inv-totals-right">
            <div className="inv-total-line">
              <span>Subtotal</span>
              <span>${fmt(totals.subtotal)}</span>
            </div>
            {discount > 0 && (
              <div className="inv-total-line">
                <span>Discount ({discount}%)</span>
                <span>-${fmt(totals.discountAmt)}</span>
              </div>
            )}
            <div className="inv-total-line inv-total-editable">
              <span>Discount</span>
              <div className="inv-total-input-wrap">
                <input
                  className="inv-input inv-total-input"
                  type="number" min="0" max="100"
                  value={discount}
                  onChange={e => setDiscount(parseFloat(e.target.value) || 0)}
                />
                <span className="inv-pct">%</span>
              </div>
            </div>
            <div className="inv-total-line inv-total-editable">
              <span>Tax</span>
              <div className="inv-total-input-wrap">
                <input
                  className="inv-input inv-total-input"
                  type="number" min="0"
                  value={taxRate}
                  onChange={e => {
                    userEditedTaxRate.current = true;
                    setTaxRate(parseFloat(e.target.value) || 0);
                  }}
                />
                <span className="inv-pct">%</span>
              </div>
            </div>
            {taxRate > 0 && (
              <div className="inv-total-line">
                <span>Tax ({taxRate}%)</span>
                <span>${fmt(totals.tax)}</span>
              </div>
            )}
            <div className="inv-total-line inv-total-grand">
              <span>Total</span>
              <span>${fmt(totals.total)}</span>
            </div>
            <div className="inv-total-line inv-total-editable">
              <span>Amount Paid</span>
              <div className="inv-total-input-wrap">
                <span className="inv-dollar">$</span>
                <input
                  className="inv-input inv-total-input inv-total-input-money"
                  type="number" min="0" step="0.01"
                  value={amountPaid}
                  onChange={e => setAmountPaid(parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>
            <div className="inv-total-line inv-total-balance">
              <span>Balance Due</span>
              <span>${fmt(totals.balanceDue)}</span>
            </div>
          </div>
        </div>

        {/* ── Notes & GST ── */}
        <div className="inv-notes">
          <span className="inv-label">Notes</span>
          <textarea
            className="inv-input inv-notes-input"
            value={notesToClient}
            onChange={e => setNotesToClient(e.target.value)}
            readOnly={readOnly}
            maxLength={2000}
            aria-label="Notes to client"
            placeholder="Notes — any relevant information not already covered"
            rows={3}
          />
          <div className="inv-gst-row">
            <span className="inv-label">GST #</span>
            <input
              className="inv-input inv-gst-input"
              value={gstNumber}
              onChange={e => setGstNumber(e.target.value)}
              placeholder="e.g. 811818848RT0001"
            />
          </div>
        </div>

        {/* ── Footer Actions ── */}
        <div className="inv-footer">
          <button className="inv-btn-cancel" onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</button>
          {!readOnly && (
            <button
              className="inv-btn-download"
              onClick={handleSubmit}
              disabled={isSaving || allLineItems.length === 0}
            >
              {isSaving ? 'Saving...' : existingInvoice ? 'Update & Download' : 'Save & Download'}
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

/**
 * Do not instantiate the wizard until the tenant's runtime config has RESOLVED.
 *
 * The sender fields below are seeded by `useState` initialisers, which React runs once on the
 * first render — so gating inside the component would be too late, and a wizard opened before
 * `/api/config` lands would seed the product fallback (name, address, phone, GST) and keep it
 * even after the tenant's real config arrived. On a tax document that is the wrong company's
 * GST number. Blocking here is deliberate over prefilling-then-correcting: the fields are
 * editable and an operator who sees a plausible letterhead may not check it.
 *
 * 'unavailable' renders normally — a deployment with no tenant row is a legitimate steady
 * state, and the fallback is the right answer there.
 */
const InvoiceWizardGate: React.FC<InvoiceWizardProps> = (props) => {
  const status = useRuntimeConfigStatus();
  if (status === 'pending') {
    return (
      <div className="inv-overlay">
        <div className="inv-modal inv-modal-loading">
          <p>Loading organisation settings…</p>
        </div>
      </div>
    );
  }
  return <InvoiceWizard {...props} />;
};

export default InvoiceWizardGate;
